import { inject, Injectable } from "@angular/core";
import { VehicleInfoService } from "@modules/core/services/vehicle-info.service";
import { Vehicle, VehicleBlockActivityInfo } from "@modules/shared/models";
import { firstValueFrom } from "rxjs";
import moment from "moment";

// Cache entry interface with timestamp
interface CacheEntry {
    data: VehicleBlockActivityInfo[];
    timestamp: number;
}

@Injectable({
    providedIn: 'root'
})
export class HtmlPopupHelper {

    private readonly _vehicleInfoService = inject(VehicleInfoService);

    private readonly _vehicleActivityCache = new Map<string, CacheEntry>();
    private readonly _inProgressRequests = new Map<string, Promise<VehicleBlockActivityInfo[] | undefined>>();
    private readonly _cacheLifetimeMs = 1 * 60 * 1000; // 1 minute in milliseconds

    public createBasicPoup(properties: {
        title: string
    }) {
        let html = '<div>';
        html += properties.title;
        html += '</div>';
        return html;
    }

    public async getActivitiesForVehicle(vehicle: Vehicle): Promise<VehicleBlockActivityInfo[] | undefined> {
        return this._getVehicleActivity(vehicle);
    }

    public resolveActivities(
        vehicle: Vehicle,
        activities: VehicleBlockActivityInfo[] | undefined
    ): { currentActivity?: VehicleBlockActivityInfo; nextActivity?: VehicleBlockActivityInfo } {
        if (!activities?.length) return {};

        const activeActivityIndex = activities.findIndex(va =>
            va.inventoryNumber === vehicle.inventoryNumber
            && va.routeName === vehicle.routeNumber
            && va.tripNumber === vehicle.tripNumber
        );

        let activeActivity: VehicleBlockActivityInfo | undefined;
        let nextActivity: VehicleBlockActivityInfo | undefined;

        if (activeActivityIndex === -1) {
            activeActivity = this._getEstimatedBlockActivity(activities);
            if (activeActivity) {
                const idx = activities.indexOf(activeActivity);
                nextActivity = activities[idx + 1];
            }
        } else {
            activeActivity = activities[activeActivityIndex];
            nextActivity = activities[activeActivityIndex + 1];
        }

        return { currentActivity: activeActivity, nextActivity };
    }

    private _createCacheKey(inventoryNumber: string, routeNumber: string, tripNumber: string): string {
        return `${inventoryNumber}-${routeNumber}-${tripNumber}`;
    }

    // Helper method to get cached vehicle activity or fetch new one
    private async _getVehicleActivity(vehicle: Vehicle): Promise<VehicleBlockActivityInfo[] | undefined> {
        const cacheKey = this._createCacheKey(vehicle.inventoryNumber, vehicle.routeNumber, vehicle.tripNumber);

        // Check if we already have this activity cached and it's not expired
        const cacheEntry = this._vehicleActivityCache.get(cacheKey);
        if (cacheEntry) {
            const isExpired = Date.now() - cacheEntry.timestamp > this._cacheLifetimeMs;

            if (isExpired) {
                // Remove expired entry
                this._vehicleActivityCache.delete(cacheKey);
            } else {
                return cacheEntry.data;
            }
        }

        // Check if there's already a request in progress for this cache key
        if (this._inProgressRequests.has(cacheKey)) {
            // Wait for the existing request to complete
            return await this._inProgressRequests.get(cacheKey);
        }

        // Create and store the promise for this request
        const requestPromise = this._fetchAndCacheVehicleActivity(cacheKey, vehicle);
        this._inProgressRequests.set(cacheKey, requestPromise);

        try {
            const result = await requestPromise;
            return result;
        } finally {
            // Clean up the in-progress request
            this._inProgressRequests.delete(cacheKey);
        }
    }

    // Helper method to fetch and cache vehicle activity
    private async _fetchAndCacheVehicleActivity(
        cacheKey: string,
        vehicle: Vehicle
    ): Promise<VehicleBlockActivityInfo[] | undefined> {
        try {
            const vehicleActivitys = (await firstValueFrom(this._vehicleInfoService.getVehicleActivities(
                vehicle.inventoryNumber,
                vehicle.trafficDate
            )))?.vehicleActivities;

            if (vehicleActivitys?.length) {
                this._vehicleActivityCache.set(cacheKey, {
                    data: vehicleActivitys,
                    timestamp: Date.now()
                });
            }

            return vehicleActivitys;
        } catch (error) {
            console.error('Failed to fetch vehicle activity:', error);
            return undefined;
        }
    }

    private _getEstimatedBlockActivity(vehicleBlockActivities: VehicleBlockActivityInfo[]): VehicleBlockActivityInfo {
        if (!vehicleBlockActivities?.length) {
            return undefined;
        }

        const now = moment();

        // Find closest block by starttime and endtime
        const itemInTimeFrame = vehicleBlockActivities.find(va => {
            const startTimeAsMoment = moment(va.startDateTime);
            const endTimeAsMoment = moment(va.endDateTime);
            return now.isAfter(startTimeAsMoment) && now.isBefore(endTimeAsMoment);
        });

        if (itemInTimeFrame) {
            return itemInTimeFrame;
        }

        // Find closest block by only starttime
        const allStartedBlockActivities = vehicleBlockActivities.filter(va => {
            const startTimeAsMoment = moment(va.startDateTime);
            return startTimeAsMoment.isAfter(now);
        });

        const itemWithStartClosestToNow = allStartedBlockActivities[0];
        if (itemWithStartClosestToNow) {
            return itemWithStartClosestToNow;
        }

        return undefined;
    }
}